// src/components/AnyToAnyConverter.js
import React, { useState, useRef } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { convertFile } from "../services/apiService";
import { IoCloudUploadOutline, IoDownloadOutline } from 'react-icons/io5';

// --- Category & Format Definitions ---
const categoryMapping = {
  image: ["png", "jpg", "jpeg", "webp", "bmp", "svg", "tiff", "gif", "heic"],
  document: ["pdf", "docx", "txt", "rtf", "odt", "html", "epub"],
  video: ["mp4", "avi", "mkv", "mov", "flv", "webm"],
  audio: ["mp3", "wav", "aac", "flac", "ogg", "m4a"],
};

const formatOptions = {
  image: ["png", "jpg", "jpeg", "webp", "bmp", "svg", "tiff", "gif", "heic"],
  document: ["pdf", "docx", "txt", "rtf", "odt", "html", "epub"],
  video: ["mp4", "avi", "mkv", "mov", "flv", "webm"],
  audio: ["mp3", "wav", "aac", "flac", "ogg", "m4a"],
};

const detectCategory = (extension) => {
  extension = extension.toLowerCase();
  for (const [category, extensions] of Object.entries(categoryMapping)) {
    if (extensions.includes(extension)) return category;
  }
  return 'document';
};

const getStatusIndicator = (status) => {
    switch (status) {
        case 'pending': return <span className="text-xs font-medium text-gray-400">Pending</span>;
        case 'converting': return <span className="text-xs font-medium text-yellow-400">Converting...</span>;
        case 'success': return <span className="text-xs font-medium text-green-400">Success</span>;
        case 'error': return <span className="text-xs font-medium text-red-400">Error</span>;
        default: return null;
    }
};

const AnyToAnyConverter = () => {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const handleFiles = (selectedFiles) => {
    const newFiles = [];
    for (const file of selectedFiles) {
      const parts = file.name.split(".");
      if (parts.length < 2) continue;
      const ext = parts.pop().toLowerCase();
      const category = detectCategory(ext);
      
      newFiles.push({ 
        id: `${file.name}-${Date.now()}-${Math.random()}`, // More robust unique ID
        file, 
        inputFormat: ext, 
        category, 
        outputFormat: formatOptions[category].find(f => f !== ext) || formatOptions[category][0], 
        status: "pending", 
        convertedBlob: null 
      });
    }
    setError("");
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleFileChange = (e) => {
      if(e.target.files) handleFiles(Array.from(e.target.files));
  };

  // ✅ THIS IS THE CORRECTED FUNCTION
  const startConversion = async (id, newFormat) => {
    // Use the functional form of setState to get the most up-to-date state
    setFiles(currentFiles => {
      // Find the file to convert from the CURRENT state
      const fileToConvert = currentFiles.find(f => f.id === id);
      if (!fileToConvert) return currentFiles;

      // Immediately call the API with the correct file object
      convertFile(fileToConvert.file, fileToConvert.category, newFormat)
        .then(blob => {
          // On success, update the state again
          setFiles(prev => prev.map(f => f.id === id ? { ...f, convertedBlob: blob, status: 'success' } : f));
        })
        .catch(err => {
          console.error("Conversion error:", err);
          // On error, update the state again
          setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'error' } : f));
        });

      // Return the new state for the initial "converting" status update
      return currentFiles.map(f => f.id === id ? { ...f, outputFormat: newFormat, status: 'converting' } : f);
    });
  };
  
  const downloadAll = async () => {
    const zip = new JSZip();
    files.forEach(f => {
      if (f.convertedBlob) {
        zip.file(`${f.file.name.split('.').slice(0, -1).join('.')}.${f.outputFormat}`, f.convertedBlob);
      }
    });
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "oolook_converted_files.zip");
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
        fileInputRef.current.value = null;
        fileInputRef.current.click();
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6">
      <div className="bg-dark-card/40 backdrop-blur-lg border border-white/10 rounded-3xl p-8">
        <input type="file" ref={fileInputRef} onChange={handleFileChange} multiple className="hidden" />

        {files.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-12">
            <IoCloudUploadOutline className="h-16 w-16 text-dark-text-secondary/50 mb-4" />
            <h3 className="text-xl font-semibold text-dark-text-primary">Drag & drop files here</h3>
            <p className="mt-2 text-dark-text-secondary">Images, Videos, Documents & More</p>
            <button onClick={triggerFileInput} className="mt-4 rounded-md bg-gradient-to-r from-dark-accent-start to-dark-accent-end px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90">
              Select Files
            </button>
          </div>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-dark-text-secondary uppercase border-b border-white/10">
                  <tr>
                    <th scope="col" className="px-6 py-3">Filename</th>
                    <th scope="col" className="px-6 py-3">From</th>
                    <th scope="col" className="px-6 py-3">To</th>
                    <th scope="col" className="px-6 py-3">Status</th>
                    <th scope="col" className="px-6 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((f) => (
                    <tr key={f.id} className="border-b border-white/10">
                      <td className="px-6 py-4 font-medium text-dark-text-primary whitespace-nowrap">{f.file.name}</td>
                      <td className="px-6 py-4 uppercase">{f.inputFormat}</td>
                      <td className="px-6 py-4">
                        <select
                          value={f.outputFormat}
                          onChange={(e) => startConversion(f.id, e.target.value)}
                          disabled={f.status === 'converting'}
                          className="bg-dark-card/60 border border-white/10 rounded-md px-2 py-1 text-xs text-dark-text-primary focus:ring-1 focus:ring-dark-accent-start outline-none"
                        >
                          {formatOptions[f.category].map(fmt => <option key={fmt} value={fmt}>{fmt.toUpperCase()}</option>)}
                        </select>
                      </td>
                      <td className="px-6 py-4">{getStatusIndicator(f.status)}</td>
                      <td className="px-6 py-4 text-right">
                        {f.status === 'success' && <a href={URL.createObjectURL(f.convertedBlob)} download={`${f.file.name.split('.').slice(0, -1).join('.')}.${f.outputFormat}`} className="font-medium text-dark-accent-start hover:underline">Download</a>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-between items-center">
                <button onClick={triggerFileInput} className="rounded-md bg-white/10 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-white/20">
                    Add More Files
                </button>
                <button onClick={downloadAll} disabled={!files.some(f=>f.status === 'success')} className="rounded-md bg-gradient-to-r from-dark-accent-start to-dark-accent-end px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50">
                    <IoDownloadOutline className="inline -mt-1 mr-2" />
                    Download All (.zip)
                </button>
            </div>
          </div>
        )}
        {error && <p className="text-center text-sm text-red-400 mt-4">{error}</p>}
      </div>
    </div>
  );
};

export default AnyToAnyConverter;